/**
 * Plain-English descriptions of S-57 attribute codes.
 *
 * This exists because of a recurring complaint about competing apps: users
 * cannot find a legend and cannot tell what a symbol means. Vector charts make
 * the fix nearly free — every feature already carries its attributes, so tapping
 * one can explain itself in words instead of sending the skipper to a PDF.
 *
 * Codes are from the IHO S-57 Object Catalogue. Unknown codes are shown as
 * "code N" rather than hidden: an unexplained value is still information, and
 * silently dropping it would be worse.
 */

export const WATER_LEVELS: Record<number, string> = {
  1: 'partly submerged at high water',
  2: 'always dry',
  3: 'always submerged',
  4: 'covers and uncovers',
  5: 'awash at chart datum',
  6: 'subject to inundation',
  7: 'floating',
};

export const WRECK_CATEGORIES: Record<number, string> = {
  1: 'non-dangerous wreck',
  2: 'dangerous wreck',
  3: 'distributed remains',
  4: 'mast showing',
  5: 'hull showing',
};

export const BUOY_SHAPES: Record<number, string> = {
  1: 'conical',
  2: 'can',
  3: 'spherical',
  4: 'pillar',
  5: 'spar',
  6: 'barrel',
  7: 'superbuoy',
  8: 'ice buoy',
};

export const COLOURS: Record<string, string> = {
  1: 'white',
  2: 'black',
  3: 'red',
  4: 'green',
  5: 'blue',
  6: 'yellow',
  7: 'grey',
  8: 'brown',
  9: 'amber',
  10: 'violet',
  11: 'orange',
  12: 'magenta',
  13: 'pink',
};

export const LATERAL_MARKS: Record<number, string> = {
  1: 'port-hand mark',
  2: 'starboard-hand mark',
  3: 'preferred channel to starboard',
  4: 'preferred channel to port',
};

export const CARDINAL_MARKS: Record<number, string> = {
  1: 'north cardinal',
  2: 'east cardinal',
  3: 'south cardinal',
  4: 'west cardinal',
};

export const LIGHT_CHARACTERS: Record<number, string> = {
  1: 'fixed',
  2: 'flashing',
  3: 'long-flashing',
  4: 'quick-flashing',
  5: 'very quick-flashing',
  6: 'ultra quick-flashing',
  7: 'isophase',
  8: 'occulting',
  9: 'interrupted quick-flashing',
  10: 'interrupted very quick-flashing',
  11: 'interrupted ultra quick-flashing',
  12: 'morse',
  13: 'fixed and flashing',
  14: 'flashing and long-flashing',
  25: 'alternating',
};

export const ANCHORAGE_CATEGORIES: Record<number, string> = {
  1: 'unrestricted anchorage',
  2: 'deep water anchorage',
  3: 'tanker anchorage',
  4: 'explosives anchorage',
  5: 'quarantine anchorage',
  6: 'seaplane anchorage',
  7: 'small craft anchorage',
  8: 'small craft mooring area',
  9: '24-hour anchorage',
  10: 'limited-period anchorage',
};

export const RESTRICTIONS: Record<string, string> = {
  1: 'anchoring prohibited',
  2: 'anchoring restricted',
  3: 'fishing prohibited',
  4: 'fishing restricted',
  5: 'trawling prohibited',
  6: 'trawling restricted',
  7: 'entry prohibited',
  8: 'entry restricted',
  9: 'dredging prohibited',
  10: 'dredging restricted',
  13: 'no wake',
  14: 'area to be avoided',
  ics: 'restricted',
};

export const SEABED_SURFACES: Record<string, string> = {
  1: 'mud',
  2: 'clay',
  3: 'silt',
  4: 'sand',
  5: 'stone',
  6: 'gravel',
  7: 'pebbles',
  8: 'cobbles',
  9: 'rock',
  11: 'lava',
  14: 'coral',
  17: 'shells',
  18: 'boulder',
};

/** Canonical layer name to a human label. */
export const LAYER_LABELS: Record<string, string> = {
  depth_area: 'Depth area',
  depth_contour: 'Depth contour',
  sounding: 'Sounding',
  hazard: 'Hazard',
  overhead_obstruction: 'Overhead obstruction',
  land_area: 'Land',
  coastline: 'Coastline',
  shoreline_construction: 'Shoreline structure',
  inland_water: 'Inland water',
  built_up_area: 'Built-up area',
  buoy: 'Buoy',
  beacon: 'Beacon',
  light: 'Light',
  fog_signal: 'Fog signal',
  landmark: 'Landmark',
  restricted_area: 'Restricted area',
  anchorage: 'Anchorage',
  traffic_scheme: 'Traffic scheme',
  cable_pipeline: 'Cable or pipeline',
  seabed_area: 'Seabed',
  sea_area: 'Sea area',
  coverage: 'Chart coverage',
};

/** `kind` discriminator to a human label. */
export const KIND_LABELS: Record<string, string> = {
  rock: 'Rock',
  wreck: 'Wreck',
  obstruction: 'Obstruction',
  bridge: 'Bridge',
  overhead_cable: 'Overhead cable',
  pylon: 'Pylon',
  lateral: 'Lateral',
  cardinal: 'Cardinal',
  isolated_danger: 'Isolated danger',
  safe_water: 'Safe water',
  special: 'Special',
  daymark: 'Daymark',
  cable: 'Submarine cable',
  pipeline: 'Submarine pipeline',
  cable_area: 'Cable area',
  lake: 'Lake',
  river: 'River',
  restricted: 'Restricted',
  caution: 'Caution',
  military: 'Military practice',
  fishing: 'Fishing',
  aquaculture: 'Aquaculture',
  offshore_production: 'Offshore production',
  dumping_ground: 'Dumping ground',
  lane: 'Traffic lane',
  boundary: 'Scheme boundary',
  separation_zone: 'Separation zone',
  crossing: 'Crossing',
  roundabout: 'Roundabout',
  recommended_track: 'Recommended track',
  shoreline: 'Shoreline structure',
  pontoon: 'Pontoon',
};

/** Look a code up, falling back to something honest rather than to nothing. */
export function describeCode(
  table: Record<string | number, string>,
  value: unknown,
): string | null {
  if (value === null || value === undefined || value === '') return null;
  const key = String(value);
  return table[key] ?? `code ${key}`;
}

/**
 * Describe a comma-joined multi-valued attribute, e.g. COLOUR "3,1" -> "red,
 * white". Multi-valued attributes are flattened to strings by the pipeline
 * because MVT has no array type.
 */
export function describeCodeList(
  table: Record<string | number, string>,
  value: unknown,
): string | null {
  if (value === null || value === undefined || value === '') return null;
  const parts = String(value).split(',').map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return null;
  return parts.map((part) => table[part] ?? `code ${part}`).join(', ');
}
